import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t("auth.welcome_back", { name: user.name }));
      const fallbackByRole = { admin: "/admin", instructor: "/instructor" };
      const next = location.state?.from || fallbackByRole[user.role] || "/";
      navigate(next);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#050505]" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4"><LanguageSwitcher /></div>
        <Link to="/" className="flex items-center justify-center gap-2 mb-10">
          <Lightning size={28} weight="fill" className="text-[#E30613]" />
          <span className="font-display text-2xl uppercase font-black">FitAgenda <span className="text-[#E30613]">CH</span></span>
        </Link>

        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("auth.login_tag")}</div>
          <h1 className="font-display text-4xl uppercase font-black mt-2">{t("auth.login_title")}</h1>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.email")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.password")}</Label>
                <Link to="/forgot-password" className="text-[10px] uppercase tracking-widest text-[#E30613] hover:text-white font-bold" data-testid="login-forgot-link">
                  {t("auth.forgot_link")}
                </Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12 btn-tactical"
              data-testid="login-submit-button">
              {loading ? t("auth.logging_in") : t("auth.login_action")}
            </Button>
          </form>

          <div className="mt-6 text-xs text-zinc-500 text-center">
            {t("auth.no_account")}{" "}
            <Link to="/register" className="text-[#E30613] hover:text-white font-bold uppercase tracking-wider" data-testid="login-register-link">
              {t("auth.register_link")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
